import { isDevelopment } from "./development";
import { sanitise } from "./privacy";
import { observeRoute, type Route, type ScreenViewedProps } from "./route";
import { claimRouteSourceFailureLog, currentRoute, now, random, routeSource, send, setUser } from "./state";
import type { AnalyticsUser, EventDefinition, EventProps } from "./types";

/**
 * Declares an event. With `sampleRate` (in (0, 1]; anything else throws in development and is
 * ignored, with an error logged, elsewhere), only that fraction of calls to
 * `track` is sent, each carrying a `sample_rate` property.
 *
 * Declare events in an `analytics.ts` next to the code that fires them.
 *
 * @example
 * export const ClipDownloaded = defineEvent<{ clip_id: string }>("Clip Downloaded");
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- `{}` means "no props"
export function defineEvent<P extends EventProps<P> = {}>(
  name: string,
  options?: { sampleRate?: number },
): EventDefinition<P> {
  const sampleRate = options?.sampleRate;
  // `NaN` would otherwise send everything, and 0 nothing.
  if (sampleRate !== undefined && !(Number.isFinite(sampleRate) && sampleRate > 0 && sampleRate <= 1)) {
    const message = `sampleRate of "${name}" must be in (0, 1], got ${String(sampleRate)}`;
    // Throwing while an `analytics.ts` module loads could take down its importer
    // (e.g. the API client at boot), so outside development the event is unsampled.
    if (isDevelopment()) {
      throw new RangeError(message);
    }
    console.error(`[a3s-analytics] ${message}; sending every event.`);
    return { name, sampleRate: undefined };
  }
  return { name, sampleRate };
}

/**
 * Sends an event. Tenant, site, camera and screen are added from the current route.
 * `P` comes from the definition alone (`NoInfer`): inferring it from the props too
 * would widen an object literal's `mode: "soft"` to `string`, which a literal-union
 * definition then rejects.
 */
export function track<P extends EventProps<P>>(
  event: EventDefinition<P>,
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- `{}` means "no props"
  ...props: {} extends P ? [NoInfer<P>?] : [NoInfer<P>]
): void {
  observeRouteSource();
  const [eventProps] = props;
  record(event, eventProps);
}

const ScreenViewed = defineEvent<ScreenViewedProps>("Screen Viewed");

/**
 * Reports the current route: sends `Screen Viewed` when the template or a scope
 * param (`tenantId`, `siteId`, `cameraKey`) changes, and makes `screen`,
 * `tenant_id`, `site_id` and `camera_key` part of every later event. Other
 * params (filters, `watching`, `from`/`to`…) neither send `Screen Viewed` nor
 * reach the context: the component that sets such a param tracks it itself.
 * `useRouteAnalytics` in `@visioni/a3s-analytics/react-native` calls this.
 */
export function reportRoute(route: Route): void {
  const viewed = observeRoute(route);
  if (viewed !== undefined) {
    record(ScreenViewed, viewed);
  }
}

/** Sends `Screen Viewed` for the current route again, without `previous_screen`. */
export function resendCurrentScreen(): void {
  const route = currentRoute();
  if (route !== undefined) {
    record(ScreenViewed, { screen: route.screen });
  }
}

/**
 * Asks the route source (registered by `useRouteAnalytics`) for the route at
 * this moment, which can be newer than the last one reported: a new screen's
 * mount effects run before its root layout hears of the navigation. A newer
 * route is reported first, so its `Screen Viewed` precedes the event and the
 * event carries its context. Without a source, or when it has no route yet,
 * the last reported route applies.
 *
 * The source reads expo-router internals, so it and the report of its route
 * must never throw into `track`'s caller (the API client tracks every
 * request). A failure falls back to the last reported route and is logged
 * once, not on every call.
 *
 * Internal (not exported from the entry points): `useRouteAnalytics` also
 * calls it while the root layout renders.
 */
export function observeRouteSource(): void {
  const source = routeSource();
  if (source === undefined) {
    return;
  }
  try {
    const route = source();
    if (route !== undefined) {
      reportRoute(route);
    }
  } catch (error) {
    if (claimRouteSourceFailureLog()) {
      console.error("[a3s-analytics] Route source failed; events use the last reported route:", error);
    }
  }
}

function record<P extends EventProps<P>>(event: EventDefinition<P>, eventProps: P | undefined): void {
  const { name, sampleRate } = event;
  if (sampleRate !== undefined && random() >= sampleRate) {
    return;
  }
  send({
    name,
    properties: {
      ...currentRoute()?.context,
      ...sanitise(name, eventProps ?? {}),
      // Lets charts scale a sampled count back up to the real total.
      ...(sampleRate !== undefined && { sample_rate: sampleRate }),
    },
    time: now(),
  });
}

/**
 * Sets the user later events are sent under, with their user properties.
 * `identify(null)` resets to anonymous, on logout.
 */
export function identify(user: AnalyticsUser): void {
  setUser(user);
}
